import { defineComponent as _defineComponent } from 'vue'
import BCXTable from '@/components/BCXTable.vue';
import { computed, PropType, ref } from 'vue';
import { useMyProjects } from '@/views/my-projects/composables/useMyProjects';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import BCXUserBannerSimple from '@/components/molecules/BCXUserBannerSimple.vue';
import { getDisplayName } from '@/utils/user';
import { BcxTableFilter, BcxTableQuickFilter } from '@/models/BcxTable';
import toggleArrayItem from '@/utils/toggleArrayItem';
import InvoiceIncomingExtendedRow from '@/views/my-projects/components/invoices/InvoiceIncomingExtendedRow.vue';
import { ComponentInvoice, InvoiceStatus } from '@/models/Invoice';
import useUser from '@/mixins/useUser';
import store from '@/store';
import useInvoice from '@/views/my-projects/composables/useInvoice';
import useNotificationState from '@/state/notificationState';
import useResponsiveness from '@/mixins/useResponsiveness';
import { useRouter } from 'vue2-helpers/vue-router';
import useI18n from '@/mixins/useI18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'InvoicesTable',
  props: {
  data: {
    type: Array as PropType<ComponentInvoice[]>,
    default: () => []
  },
  type: {
    type: String as PropType<'due' | 'incoming' | 'outgoing' | 'draft'>,
    default: 'due'
  }
},
  setup(__props) {

const props = __props;



const { tc } = useI18n();
const { tUserFallback: t } = useUser('self');
const { getFormattedDate } = useFormattedDateTime();
const { fetchUpdateReadDateInvoice, invoices } = useInvoice();
const { loadNotifications } = useNotificationState();
const { isMobileWidth, isSmartphoneWidth } = useResponsiveness();
const router = useRouter();

const { isNarrow } = useMyProjects();

const isOutgoing = computed(() => props.type === 'outgoing');
const isIncoming = computed(() => props.type === 'incoming');
const isDraft = computed(() => props.type === 'draft');

const selectedInvoiceIDs = ref<string[]>([]);
const isRoutingAllowed = computed(() => ['draft'].includes(props.type));

const templateColumns = computed<string>(() => {
  switch (props.type) {
    case 'due':
      return '6fr 4fr 5fr 3fr 4.2fr';
    case 'incoming':
      return '6fr 4fr 5fr 3fr 4.2fr';
    case 'outgoing':
      return '6fr 4fr 5fr 3fr 4.2fr';
    case 'draft':
      return '6fr 4fr 5fr 7.2fr';
    default:
      return '6fr 4fr 5fr 3fr 4.2fr';
  }
});

const mobileGridAreas = computed(() => {
  switch (props.type) {
    case 'due':
      return 'user user\nreceived invoiceDocId\ndueDate status';
    case 'incoming':
      return 'user user\nreceived invoiceDocId\ndueDate status';
    case 'outgoing':
      return 'user user\nsentDate invoiceDocId\ndueDate status';
    case 'draft':
      return 'user user\nchangedDate invoiceDocId\nreference reference';
    default:
      return 'user user\nreceived invoiceDocId\ndueDate status';
  }
});

const fieldNames = computed(() => {
  switch (props.type) {
    case 'due':
      return ['user', 'received', 'invoiceDocId', 'dueDate', 'status'];
    case 'incoming':
      return ['user', 'received', 'invoiceDocId', 'dueDate', 'status'];
    case 'outgoing':
      return ['user', 'sentDate', 'invoiceDocId', 'dueDate', 'status'];
    case 'draft':
      return ['user', 'changedDate', 'invoiceDocId', 'reference'];
    default:
      return ['user', 'received', 'invoiceDocId', 'dueDate', 'status'];
  }
});

const headerFieldsMobile = computed(() => {
  switch (props.type) {
    case 'due':
      return ['user', 'received', 'invoiceDocId', 'dueDate', 'status'];
    case 'incoming':
      return ['user', 'received', 'invoiceDocId', 'dueDate', 'status'];
    case 'outgoing':
      return ['user', 'sentDate', 'invoiceDocId', 'dueDate', 'status'];
    case 'draft':
      return ['user', 'changedDate', 'invoiceDocId', 'reference'];
    default:
      return ['user', 'received', 'invoiceDocId', 'dueDate', 'status'];
  }
});

const defaultSortField = computed(() => {
  switch (props.type) {
    case 'due':
      return 'received';
    case 'incoming':
      return 'received';
    case 'outgoing':
      return 'sentDate';
    case 'draft':
      return 'changedDate';
    default:
      return null;
  }
});

const sortableFields = computed(() => (isNarrow.value ? headerFieldsMobile.value : fieldNames.value));

const headerTitles = computed(() => sortableFields.value
  .map((key) => {
    if (key === 'user') {
      return isOutgoing.value || isDraft.value ? 'to' : 'from';
    }
    if (key === 'invoiceDocId' && isSmartphoneWidth.value && (isIncoming.value || isOutgoing.value || props.type === 'due')) {
      return 'invoiceDocIdShort';
    }
    return key;
  })
  .map((key) => `${t(`invoices.table.columns.${key}_USERTYPE`)}`));

const displayValueCallback = (field: string, obj: ComponentInvoice) => {
  switch (field) {
    case 'dueDate':
      if (!obj.dueDate) return '-';
      if (['outgoing', 'incoming'].includes(props.type)) {
        const showDueDate = [InvoiceStatus.Open, InvoiceStatus.Sent, InvoiceStatus.Due].includes(obj.status);
        return showDueDate ? getFormattedDate(obj.dueDate) : '-';
      }
      return getFormattedDate(obj.dueDate);
    case 'user':
      return obj.user;
    case 'status':
      return `${t(`invoices.table.filter.${obj.status}_USERTYPE`)}`;
    case 'received':
      return obj.sentDate ? getFormattedDate(obj.sentDate) : '-';
    case 'invoiceDocId':
      return obj.invoiceDocId ? obj.invoiceDocId : '-';
    case 'reference':
      return obj.reference ? obj.reference : '-';
    case 'changedDate':
      return obj.changedDate ? getFormattedDate(obj.changedDate) : '-';
    case 'sentDate':
      return obj.sentDate ? getFormattedDate(obj.sentDate) : '-';
  }
  return '';
};

const sortValueCallback = (field: string, obj: ComponentInvoice) => {
  const viewUser = computed(() => store.getters['userStates/getUserInfo'](obj.user.userId));

  switch (field) {
    case 'dueDate':
      if (!obj.dueDate) return '';
      if (['outgoing', 'incoming'].includes(props.type)) {
        const showDueDate = [InvoiceStatus.Open, InvoiceStatus.Sent, InvoiceStatus.Due].includes(obj.status);
        return showDueDate ? obj.dueDate : '';
      }
      return obj.dueDate;
    case 'status':
      return `${t(`invoices.table.filter.${obj.status}`)}`;
    case 'user':
      return getDisplayName(viewUser.value);
    case 'received':
      return obj.sentDate ?? '';
    case 'invoiceDocId':
      return obj.invoiceDocId;
    case 'reference':
      return obj.reference;
    case 'changedDate':
      return obj.changedDate ?? '';
    case 'sentDate':
      return obj.sentDate ?? '';
  }
  return 0;
};

const searchableCallback = (search: string, obj: ComponentInvoice) => {
  const searchBlockIncoming = `${getDisplayName(obj.user)}  ${getFormattedDate(obj.created)}  ${obj.invoiceDocId} ${getFormattedDate(obj.dueDate)} ${t(
    `invoices.table.filter.${obj.status}`
  )}`.toLocaleLowerCase();
  const searchBlockOutgoing = `${getDisplayName(obj.user)}  ${getFormattedDate(obj.sentDate)} ${obj.invoiceDocId}  ${getFormattedDate(obj.dueDate)} ${t(
    `invoices.table.filter.${obj.status}`
  )}`.toLocaleLowerCase();
  const searchBlockDraft = `${getDisplayName(obj.user)}  ${getFormattedDate(obj.changedDate)} ${obj.invoiceDocId}  ${obj.reference}`.toLocaleLowerCase();

  const map = {
    incoming: searchBlockIncoming,
    outgoing: searchBlockOutgoing,
    draft: searchBlockDraft,
    due: null
  };

  const searchBlock = map[props.type];
  if (searchBlock === null) return null;

  return searchBlock.includes(search.toLocaleLowerCase());
};

function getFilterStates() {
  switch (props.type) {
    case 'incoming':
      return [InvoiceStatus.Open, InvoiceStatus.Due, InvoiceStatus.Cancelled, InvoiceStatus.Paid, InvoiceStatus.CancellationCreated];
    case 'outgoing':
      return [InvoiceStatus.Sent, InvoiceStatus.Due, InvoiceStatus.Cancelled, InvoiceStatus.Paid, InvoiceStatus.CancellationCreated];
    default:
      return [InvoiceStatus.Unpaid, InvoiceStatus.Cancelled, InvoiceStatus.Paid];
  }
}

const filter = computed(
  (): BcxTableFilter => ({
    style: 'dropdown',
    isExclusive: false,
    states: getFilterStates(),
    stateCallback: (states: string[], obj: ComponentInvoice) => states.includes(obj.status),
    labelCallback: (state: string) => `${t(`invoices.table.filter.${state}_USERTYPE`)}`
  })
);

function hasInvoiceUnreadChanges(i: ComponentInvoice): boolean {
  if (i.status === InvoiceStatus.Draft) return false; // In the draft, only the creator/user makes changes in the invoice.
  if (i.status === InvoiceStatus.Sent) return false; // The user himself can only have made changes. Note: Status sent = sender and Status open = recipient
  if (i.status === InvoiceStatus.Cancelled && i.isUserSender) return false; // The user canceled the invoice themselves.
  if (i.status === InvoiceStatus.CancellationCreated && i.isUserSender) return false; // The user canceled the invoice themselves.
  if (i.status === InvoiceStatus.Paid && (i.invoicePaidMarkedBy === 'SELLER') === i.isUserSender) return false; // The user has marked the invoice as paid themselves.

  return new Date(i.readDate || 0) < new Date(i.lastUpdated || 0);
}

function shouldDisplayInvoiceInNewFilter(invoice: ComponentInvoice, selectedInvoiceIDs: string[]): boolean {
  return selectedInvoiceIDs.includes(invoice.invoiceId) || hasInvoiceUnreadChanges(invoice);
}

const quickFilter = computed(
  (): BcxTableQuickFilter => ({
    label: tc('common.new', 2),
    isActive: false,
    stateCallback: (states: string[], obj: ComponentInvoice) => shouldDisplayInvoiceInNewFilter(obj, selectedInvoiceIDs.value),
    beforeToggleCallback: (nextStatus: boolean) => {
      if (nextStatus) selectedInvoiceIDs.value = [];
    }
  })
);

const emptyText = computed(() => {
  switch (props.type) {
    case 'incoming':
      return t('invoices.table.empty.incoming_USERTYPE');
    case 'outgoing':
      return t('invoices.table.empty.outgoing_USERTYPE');
    case 'draft':
      return t('invoices.table.empty.draft_USERTYPE');
    default:
      return t('invoices.table.empty.incoming_USERTYPE');
  }
});

const onSelectRow = async (id: string) => {
  if (isRoutingAllowed.value) return;
  selectedInvoiceIDs.value = toggleArrayItem(selectedInvoiceIDs.value, id);
  if (selectedInvoiceIDs.value.length) {
    const now = new Date().toISOString();

    await fetchUpdateReadDateInvoice(0, id, { readDate: now });
    await loadNotifications();
    // The entire object must be remapped, otherwise the dependencies of invoice with vue 2.7 will not be recalculated. Vue.set did not work.
    invoices.value = (invoices.value ?? []).map((inv) => (inv.invoiceId === id ? { ...inv, readDate: now } : inv));
  }
};

function capitalizeFirst(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function createPersistenceKey(type: string): string {
  if (type === 'due') return '';
  return `invoicesTableFilter${capitalizeFirst(type)}`;
}

function onClickOfTableRow(row: ComponentInvoice) {
  if (!isRoutingAllowed.value) onSelectRow(row.invoiceId);
  else router.push({ name: 'myProjectsInvoice', params: { invoiceId: row.invoiceId } });
}


return { __sfc: true,props, tc, t, getFormattedDate, fetchUpdateReadDateInvoice, invoices, loadNotifications, isMobileWidth, isSmartphoneWidth, router, isNarrow, isOutgoing, isIncoming, isDraft, selectedInvoiceIDs, isRoutingAllowed, templateColumns, mobileGridAreas, fieldNames, headerFieldsMobile, defaultSortField, sortableFields, headerTitles, displayValueCallback, sortValueCallback, searchableCallback, getFilterStates, filter, hasInvoiceUnreadChanges, shouldDisplayInvoiceInNewFilter, quickFilter, emptyText, onSelectRow, capitalizeFirst, createPersistenceKey, onClickOfTableRow, BCXTable, BCXUserBannerSimple, getDisplayName, InvoiceIncomingExtendedRow }
}

})